import styled from '@emotion/styled'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'react-bootstrap'

interface PaginationProps {
    currentPage: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
    onPageChange: (page: number) => void
}

const Pagination = ({ currentPage, totalPages, hasNext, hasPrev, onPageChange }: PaginationProps) => {
    const getVisiblePages = () => {
        const delta = 2
        const range: number[] = []
        const rangeWithDots: (number | string)[] = []
        let l: number | undefined

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i)
            }
        }

        range.forEach((i) => {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1)
                } else if (i - l !== 1) {
                    rangeWithDots.push('...')
                }
            }
            rangeWithDots.push(i)
            l = i
        })

        return rangeWithDots
    }

    if (totalPages <= 1) return null

    return (
        <PaginationWrapper>
            <PaginationButton
                disabled={!hasPrev}
                onClick={() => onPageChange(currentPage - 1)}
                variant="outline-primary"
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </PaginationButton>

            {getVisiblePages().map((page, index) => (
                <div key={`page-${page}-${index}`}>
                    {page === '...' ? (
                        <Dots>...</Dots>
                    ) : (
                        <PaginationButton
                            active={page === currentPage}
                            onClick={() => onPageChange(page as number)}
                            variant={page === currentPage ? 'primary' : 'outline-primary'}
                        >
                            {page}
                        </PaginationButton>
                    )}
                </div>
            ))}

            <PaginationButton
                disabled={!hasNext}
                onClick={() => onPageChange(currentPage + 1)}
                variant="outline-primary"
            >
                <FontAwesomeIcon icon={faChevronRight} />
            </PaginationButton>
        </PaginationWrapper>
    )
}

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin: 24px 0;
`

const PaginationButton = styled(Button)<{ active?: boolean }>`
    width: 40px;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-weight: ${(props) => (props.active ? 'bold' : 'normal')};

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const Dots = styled.span`
    padding: 0 8px;
    font-weight: bold;
    color: #6c757d;
`

export default Pagination
